import { registerSW } from 'virtual:pwa-register';
import { CONFIG } from './config.js';
import { createViewer } from './cesium/viewer.js';
import { FlightManager } from './cesium/flightManager.js';
import { FlightStore } from './store/flightStore.js';
import { FlightDataService } from './data/flightService.js';
import { schedulePrefetch } from './data/tilePrefetch.js';
import { setupControls } from './ui/controls.js';
import { setupFilterPanel } from './ui/filterPanel.js';
import { setupQuickFilter } from './ui/quickFilter.js';
import { setupMapActions } from './ui/mapActions.js';
import { setupTiltSlider } from './ui/tiltSlider.js';
import { setupTimeline } from './ui/timeline.js';
import { setupColorScale } from './ui/colorScale.js';
import { setupHomeMarker } from './ui/homeMarker.js';
import { setupHomeAlarm } from './ui/homeAlarm.js';
import { HomeState } from './ui/homeState.js';
import { setupTouchSelect } from './ui/touchSelect.js';
import { InfoPanel } from './ui/infoPanel.js';
import { SpotlightState } from './data/spotlightState.js';
import { AiScreener } from './data/aiScreener.js';
import { StatusBar } from './ui/statusBar.js';
import { getTheme, onThemeChange } from './ui/theme.js';

// Register PWA service worker (auto-updates in background)
registerSW({ immediate: true });

async function init() {
  console.log('Initializing STR FlyRadar (Stuttgart)...');

  const theme = getTheme();

  // Create Cesium viewer (3D-Terrain, falls ion token present)
  const { viewer, terrainEnabled, setBasemapTheme } =
    await createViewer('cesiumContainer', CONFIG, theme);

  const flightStore = new FlightStore();

  const flightManager = new FlightManager(viewer, flightStore, {
    pollIntervalMs: CONFIG.data.pollIntervalMs,
    trailDurationMs: CONFIG.viz.trailMinutes * 60 * 1000,
    center: CONFIG.region.center,
    radiusKm: CONFIG.data.radiusKm,
    // Gespeicherte Darstellung (2D-Icons / 3D-Modelle), Standard: 2D
    iconMode: localStorage.getItem('frm-view-mode') || '2d'
  });
  flightManager.altitudeExaggeration = terrainEnabled
    ? CONFIG.viz.altitudeExaggerationTerrain
    : CONFIG.viz.altitudeExaggerationFlat;
  flightManager.setTheme(theme);

  // Theme-Wechsel: Basemap + Entity-Farben nachziehen
  onThemeChange((nextTheme) => {
    setBasemapTheme(nextTheme);
    flightManager.setTheme(nextTheme);
  });

  const statusBar = new StatusBar();
  const infoPanel = new InfoPanel();
  statusBar.setRegion(CONFIG.region.name);

  // Spotlight: automatischer Hintergrund-Check auf „coole“ Flugzeuge. Der
  // Screener klassifiziert (kostenlose Heuristik + billiges KI-Modell), der
  // Zustand versorgt Karten-Badge, Prioritäts-Filter und Panel-Badge.
  const spotlightState = new SpotlightState();
  flightManager.setSpotlightState(spotlightState);
  infoPanel.setSpotlightState(spotlightState);
  const aiScreener = new AiScreener(flightStore, spotlightState);

  const flightService = new FlightDataService({
    center: CONFIG.region.center,
    radiusKm: CONFIG.data.radiusKm,
    pollIntervalMs: CONFIG.data.pollIntervalMs,
    historyDurationMs: CONFIG.data.historyMinutes * 60 * 1000,
    staleAfterMs: CONFIG.data.staleAfterMs,
    airport: CONFIG.airport // für die ▲/▼ STR-Kennzeichnung
  });

  flightService.on('update', (flights) => flightStore.updateFlights(flights));
  flightService.on('status', (status) => statusBar.setDataStatus(status));

  setupControls(viewer, flightManager, flightStore, CONFIG, flightService);
  setupFilterPanel(flightManager, flightService);
  setupQuickFilter(flightManager);
  setupMapActions(viewer, CONFIG);
  setupTiltSlider(viewer);
  setupTimeline(flightManager, { historyMinutes: CONFIG.data.historyMinutes });
  setupColorScale(flightManager);
  const homeState = new HomeState();
  setupHomeMarker(viewer, homeState);
  // Tiefflug-Alarm: oranger Button unten rechts, wenn ein Flug tief über dem
  // Zuhause (innerhalb des Alarm-Radius) hinwegzieht → tippen öffnet Details
  setupHomeAlarm(viewer, flightStore, flightManager, homeState);
  // Größere Trefferzone beim Antippen eines Flugzeugs (v.a. mobil)
  setupTouchSelect(viewer);

  // Schließt der Nutzer das Panel (X/Swipe), auch die Cesium-Auswahl lösen —
  // sonst blendet das nächste Poll-Update (siehe unten) das Panel wieder ein.
  infoPanel.onClose = () => { viewer.selectedEntity = undefined; };

  // Handle flight selection
  viewer.selectedEntityChanged.addEventListener(() => {
    const selected = viewer.selectedEntity;
    if (selected && selected.flightData) {
      infoPanel.show(selected.flightData);
      flightManager.setSelected(selected.flightData.id);
    } else {
      infoPanel.hide();
      flightManager.setSelected(null);
    }
  });

  // Update info panel + flight count when data updates
  flightStore.on('update', () => {
    const selected = viewer.selectedEntity;
    if (selected && selected.flightData) {
      const flight = flightStore.getFlight(selected.flightData.id);
      if (flight) {
        selected.flightData = flight;
        infoPanel.show(flight);
      }
    }
    statusBar.updateFlightCount(flightStore.getFlightCount());
    statusBar.setLastUpdate();
  });

  // Die letzten 30 Minuten vom Server-Logger laden (falls deployed) —
  // Trails + Zeitleiste sind damit sofort nach dem Öffnen gefüllt
  flightService.seedHistory(CONFIG.data.historyUrl);

  flightService.start();

  // Offline-Karte im Hintergrund aktuell halten (max. 1× pro Woche)
  schedulePrefetch({
    center: CONFIG.region.center,
    zoomRadii: CONFIG.offline.zoomRadii,
    theme,
    intervalDays: CONFIG.offline.autoPrefetchDays
  });

  // Debug-Handle für Konsole/E2E-Tests (bewusst ohne UI-Auswirkung)
  window.__app = { viewer, flightManager, flightStore, flightService, homeState, spotlightState, aiScreener };

  console.log('Initialization complete');
}

init().catch(error => {
  console.error('Failed to initialize:', error);
  document.body.innerHTML = `
    <div style="display:flex;align-items:center;justify-content:center;height:100vh;color:#c62828;font-size:18px;text-align:center;padding:20px;">
      <div>
        <h2>Initialisierung fehlgeschlagen</h2>
        <p style="color:#888;margin-top:10px;">${error.message}</p>
      </div>
    </div>
  `;
});
